import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChannelCatalog, ChannelCatalogItem } from '@aquabox/shared';
import { api } from './client';
import type { Product } from './types';

/**
 * Ürünler ekranı: tek kalem görünüm + kanal kataloğu. Sunucu uçları:
 *   - `GET  /api/products/overview`                 (ürün + kanal fiyatı + kataloglar)
 *   - `POST /api/integrations/:source/catalog/refresh` ("Kataloğu yenile", salt okuma)
 *   - `PUT  /api/channels/:source/catalog-map`       (katalog satırı → ürün eşlemesi)
 * Para alanları STRING'dir; panel hesap yapmaz, "farklı mı" bilgisi sunucudan gelir.
 */
export type CatalogSource = 'HAYAT_PANEL' | 'GETIR_SU';

export interface ChannelPrice extends ChannelCatalogItem {
  source: CatalogSource;
  /** Kanal fiyatı bizim satış fiyatımızdan farklı mı (sunucu hesaplar). */
  differs: boolean;
}

export type ProductWithChannels = Product & { channelPrices: ChannelPrice[] };

export interface CatalogView {
  source: CatalogSource;
  /** Hiç çekilmediyse null. */
  fetchedAt: string | null;
  items: (ChannelCatalogItem & { productId: string | null })[];
}

export interface ProductsOverview {
  items: ProductWithChannels[];
  catalogs: CatalogView[];
}

export function useProductsOverview() {
  return useQuery({
    queryKey: ['products-overview'],
    queryFn: () => api.get<ProductsOverview>('/api/products/overview'),
  });
}

export function useCatalogMutations() {
  const qc = useQueryClient();
  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: ['products-overview'] });
  return {
    refresh: useMutation({
      mutationFn: (source: CatalogSource) =>
        api.post<ChannelCatalog>(`/api/integrations/${source}/catalog/refresh`, {}),
      onSuccess: invalidate,
    }),
    map: useMutation({
      mutationFn: (v: { source: CatalogSource; externalId: string; productId: string | null }) =>
        api.put(`/api/channels/${v.source}/catalog-map`, {
          externalId: v.externalId,
          productId: v.productId,
        }),
      onSuccess: invalidate,
    }),
  };
}
